import React, { useState } from 'react';
import { ChevronLeft, Check } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';

const responseOptions = [
  {
    key: 'project',
    icon: '🚀',
    title: 'Full Project',
    description: 'Complete project planning and execution',
    badge: 'Portfolio Ready',
    badgeColor: 'bg-teal-100 text-teal-600',
    color: 'teal'
  },
  {
    key: 'big3',
    icon: '📋',
    title: 'Weekly Goal',
    description: 'Add to my regular activities',
    badge: 'Ongoing',
    badgeColor: 'bg-azure-100 text-azure-600',
    color: 'azure'
  },
  {
    key: 'quick',
    icon: '⚡',
    title: 'Quick Start',
    description: 'Begin with initial research',
    badge: 'Today',
    badgeColor: 'bg-orange-100 text-orange-500',
    color: 'orange'
  },
  {
    key: 'later',
    icon: '⏰',
    title: 'Save for Later',
    description: 'Interested but timing isn\'t right',
    badge: 'Future',
    badgeColor: 'bg-stone-100 text-stone-600',
    color: 'stone'
  },
  {
    key: 'learn',
    icon: '🔍',
    title: 'Research Phase',
    description: 'Learn more before committing',
    badge: 'Exploring',
    badgeColor: 'bg-violet-100 text-violet-600',
    color: 'violet'
  },
  {
    key: 'pass',
    icon: '✋',
    title: 'Not Interested',
    description: 'Not a good fit right now',
    badge: 'Pass',
    badgeColor: 'bg-red-100 text-red-600',
    color: 'red'
  }
];

const helpOptions = [
  { id: 'materials', label: 'Materials & Budget', category: 'planning' },
  { id: 'workspace', label: 'Workspace Setup', category: 'planning' },
  { id: 'time', label: 'Time Management', category: 'planning' },
  { id: 'getting-started', label: 'Getting Started', category: 'support' },
  { id: 'research', label: 'Research Resources', category: 'resources' },
  { id: 'mentorship', label: 'Mentorship', category: 'support' },
];

export default function MobileEngagementInterface({ 
  recommendation, 
  onClose, 
  onSubmitResponse,
  currentUser 
}) {
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [selectedHelp, setSelectedHelp] = useState([]);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  if (!recommendation) return null;

  const handleResponseSelect = (responseKey) => {
    setSelectedResponse(responseKey);
    
    // Clear help selection if selecting "pass" or "later"
    if (responseKey === 'pass' || responseKey === 'later') {
      setSelectedHelp([]);
    }
  };

  const toggleHelp = (helpId) => {
    setSelectedHelp(prev => 
      prev.includes(helpId) 
        ? prev.filter(id => id !== helpId)
        : [...prev, helpId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedResponse) return;
    
    setIsSubmitting(true);
    
    try {
      const responseData = {
        recommendationId: recommendation.id,
        responseType: selectedResponse,
        helpRequested: selectedHelp.map(id => helpOptions.find(h => h.id === id)?.label).filter(Boolean),
        comment: comment.trim(),
        timing: getTimingFromResponse(selectedResponse),
      };

      await onSubmitResponse(responseData);
      setShowSuccess(true);
      
      // Auto-close after showing success
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Error submitting response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTimingFromResponse = (responseType) => {
    switch (responseType) {
      case 'quick': return 'This week';
      case 'project': return 'Next 2-4 weeks';
      case 'big3': return 'Ongoing weekly';
      case 'learn': return 'Next few weeks';
      case 'later': return 'Future';
      default: return '';
    }
  };

  const selectedOption = responseOptions.find(opt => opt.key === selectedResponse);
  const showHelpSection = selectedResponse && ['project', 'big3', 'quick', 'learn'].includes(selectedResponse);

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <div className="text-6xl mb-6 text-teal-500">✓</div>
          <h2 className="text-2xl font-bold text-stone-900 mb-3">Response Submitted!</h2>
          <p className="text-stone-600 mb-8 max-w-sm">
            Your parent will review your response and provide any requested support
          </p>
          <div className="space-y-3">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl"
            >
              Back to Ideas
            </button>
            <button
              onClick={() => {/* Navigate to projects */}}
              className="w-full px-6 py-3 bg-teal-600 text-white font-semibold rounded-xl"
            >
              View My Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-5 py-4 flex-shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={onClose}
            className="p-2 -ml-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-stone-900 truncate">
              {recommendation.title}
            </h1>
            <p className="text-sm text-stone-500">
              Suggested by {recommendation.authorName || 'Parent'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-5 space-y-6">
          {/* Main Question */}
          <div className="text-center">
            <h2 className="text-xl font-semibold text-stone-900 mb-2">
              How do you want to engage?
            </h2>
            <p className="text-sm text-stone-600">
              Choose your level of commitment
            </p>
          </div>

          {/* Response Options Grid */}
          <div className="grid grid-cols-2 gap-3">
            {responseOptions.map((option) => (
              <button
                key={option.key}
                onClick={() => handleResponseSelect(option.key)}
                className={`relative p-4 rounded-xl border-2 transition-all text-left min-h-[100px] flex flex-col justify-between ${
                  selectedResponse === option.key
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-stone-200 bg-stone-50 hover:border-stone-300 active:scale-98'
                }`}
              >
                {/* Selection indicator */}
                {selectedResponse === option.key && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-teal-500 text-white rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </div>
                )}
                
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{option.icon}</span>
                    <span className="font-semibold text-sm text-stone-900">
                      {option.title}
                    </span>
                  </div>
                  <p className="text-xs text-stone-600 leading-tight mb-3">
                    {option.description}
                  </p>
                </div>
                
                <div className={`${option.badgeColor} px-2 py-1 rounded text-xs font-semibold self-start`}>
                  {option.badge}
                </div>
              </button>
            ))}
          </div>

          {/* Help Section */}
          {showHelpSection && (
            <div className="bg-stone-50 rounded-xl p-4">
              <h3 className="font-semibold text-sm text-stone-900 mb-3 text-center">
                What support do you need?
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {helpOptions.map((helpOption) => (
                  <button
                    key={helpOption.id}
                    onClick={() => toggleHelp(helpOption.id)}
                    className={`relative p-3 rounded-lg border transition-all text-center min-h-[48px] flex items-center justify-center text-xs font-medium ${
                      selectedHelp.includes(helpOption.id)
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300 active:scale-95'
                    }`}
                  >
                    {selectedHelp.includes(helpOption.id) && (
                      <div className="absolute top-1 right-1 w-4 h-4 bg-teal-500 text-white rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5" />
                      </div>
                    )}
                    {helpOption.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comment Section */}
          {selectedResponse && (
            <div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Questions, modifications, or timeline thoughts?"
                rows={4}
                className="w-full p-4 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex-shrink-0 p-5 border-t border-stone-200 bg-white">
        <button
          onClick={handleSubmit}
          disabled={!selectedResponse || isSubmitting}
          className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
            selectedResponse && !isSubmitting
              ? 'bg-teal-600 text-white hover:bg-teal-700 active:scale-98'
              : 'bg-stone-300 text-stone-500 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Sending Response...
            </span>
          ) : (
            'Send Response'
          )}
        </button>
      </div>

      {/* Preview of selection */}
      {selectedOption && (
        <div className="fixed bottom-20 left-4 right-4 bg-white border border-stone-200 rounded-xl p-3 shadow-lg z-10">
          <div className="flex items-center gap-3">
            <span className="text-xl">{selectedOption.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-stone-900">
                {selectedOption.title}
              </div>
              {selectedHelp.length > 0 && (
                <div className="text-xs text-stone-600">
                  Support: {selectedHelp.length} item{selectedHelp.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
            <div className={`${selectedOption.badgeColor} px-2 py-1 rounded text-xs font-semibold`}>
              {selectedOption.badge}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Utility component for rendering in routes or as a modal
export function MobileEngagementModal({ 
  recommendationId, 
  onClose, 
  currentUser 
}) {
  const { recsIdeas, updateIdeaStatus } = useFamilyContext();
  const recommendation = recsIdeas?.find(idea => idea.id === recommendationId);

  const handleSubmitResponse = async (responseData) => {
    // Use enhanced response data format
    await updateIdeaStatus(
      recommendationId, 
      currentUser.id, 
      responseData.responseType,
      {
        helpRequested: responseData.helpRequested,
        comment: responseData.comment,
        timing: responseData.timing,
        timestamp: new Date().toISOString()
      }
    );
  };

  if (!recommendation) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-stone-900 mb-2">
            Recommendation not found
          </h2>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-xl"
          >
            Back to Ideas
          </button>
        </div>
      </div>
    );
  }

  return (
    <MobileEngagementInterface
      recommendation={recommendation}
      onClose={onClose}
      onSubmitResponse={handleSubmitResponse}
      currentUser={currentUser}
    />
  );
}